import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface EngagementStats {
  connections_made: number;
  messages_sent: number;
  events_attended: number;
  opportunities_viewed: number;
  profile_views_received: number;
  directory_searches: number;
  logins_count: number;
  period_start: string;
}

export const useEngagement = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get current month engagement stats
  const currentMonth = useQuery({
    queryKey: ['engagement', user?.id, 'current'],
    queryFn: async (): Promise<EngagementStats | null> => {
      if (!user) return null;

      const periodStart = new Date();
      periodStart.setDate(1);
      periodStart.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('member_engagement')
        .select('*')
        .eq('user_id', user.id)
        .eq('period_start', periodStart.toISOString().split('T')[0])
        .maybeSingle();

      if (error) {
        console.error('[useEngagement] Error:', error);
        return null;
      }

      return data as EngagementStats | null;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Get last 6 months for trend
  const history = useQuery({
    queryKey: ['engagement', user?.id, 'history'],
    queryFn: async (): Promise<EngagementStats[]> => {
      if (!user) return [];

      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      sixMonthsAgo.setDate(1);

      const { data, error } = await supabase
        .from('member_engagement')
        .select('*')
        .eq('user_id', user.id)
        .gte('period_start', sixMonthsAgo.toISOString().split('T')[0])
        .order('period_start', { ascending: true });

      if (error) {
        console.error('[useEngagement] History error:', error);
        return [];
      }

      return (data || []) as EngagementStats[];
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  // Increment a field
  const trackEvent = useMutation({
    mutationFn: async (field: string) => {
      if (!user) return;
      const { error } = await supabase.rpc('increment_engagement', {
        p_user_id: user.id,
        p_field: field,
      });
      if (error) console.error('[useEngagement] Track error:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement', user?.id] });
    },
  });

  // Compute a simple "engagement score" from current month
  const computeScore = (stats: EngagementStats | null): number => {
    if (!stats) return 0;
    return Math.min(100,
      stats.connections_made * 15 +
      stats.messages_sent * 5 +
      stats.events_attended * 20 +
      stats.opportunities_viewed * 3 +
      stats.profile_views_received * 2 +
      stats.logins_count * 1
    );
  };

  return {
    currentMonth: currentMonth.data,
    history: history.data || [],
    isLoading: currentMonth.isLoading,
    trackEvent: trackEvent.mutate,
    computeScore,
  };
};
